import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ArrowLeft, PencilSimple, Power, Phone, Envelope, Calendar, User,
    ClipboardText, Stethoscope, Storefront, Globe, Trash, Warning,
    ArrowRight, Crown, ClockClockwise, MagnifyingGlass, Sparkle, Clock,
} from "@phosphor-icons/react";
import { HealthScorePill } from "@/components/admin/HealthScorePill";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";
import { statusLabel as getStatusLabel } from "@/lib/caseStatus";
import { SessionsPanel } from "@/components/admin/coe/SessionsPanel";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";

/* ===========================================================================
   AdminPatientProfile — Path B follow-up for patients (2026-02-26)
   ---------------------------------------------------------------------------
   Mirrors the doctor profile architecture at /admin/patients/:id. 5 tabs:
     Overview · Cases · Appointments · Account · Audit log
   + Decommission danger zone on Overview.
   =========================================================================== */

const TABS = [
    { id: "overview",     label: "Overview",     icon: User },
    { id: "cases",        label: "Cases",        icon: ClipboardText },
    { id: "appointments", label: "Appointments", icon: Calendar },
    { id: "account",      label: "Account",      icon: Crown },
    { id: "sessions",     label: "Sessions",     icon: Clock },
    { id: "audit",        label: "Audit log",    icon: ClockClockwise },
];

const GENDERS = ["", "Female", "Male", "Other", "Prefer not to say"];

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const CASE_STATUS_STYLES = {
    draft: "bg-secondary text-foreground border-border",
    submitted: "bg-sky-100 text-sky-700 border-sky-200",
    in_review: "bg-violet-100 text-violet-700 border-violet-200",
    planning: "bg-indigo-100 text-indigo-700 border-indigo-200",
    plan_review: "bg-violet-100 text-violet-700 border-violet-200",
    approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_production: "bg-amber-100 text-amber-800 border-amber-200",
    shipped: "bg-cyan-100 text-cyan-800 border-cyan-200",
    delivered: "bg-teal-100 text-teal-800 border-teal-200",
    in_treatment: "bg-blue-100 text-blue-700 border-blue-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-100 text-rose-700 border-rose-200",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

const APPT_STATUS_STYLES = {
    scheduled: "bg-sky-100 text-sky-700 border-sky-200",
    confirmed: "bg-indigo-100 text-indigo-700 border-indigo-200",
    arrived: "bg-amber-100 text-amber-700 border-amber-200",
    completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200",
    no_show: "bg-rose-100 text-rose-700 border-rose-200",
};


export default function AdminPatientProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("overview");
    const [toggleOpen, setToggleOpen] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/admin/patients/${id}`)
            .then((r) => setPatient(r.data))
            .catch(() => setPatient(null))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [id]);

    const toggleActive = async (comment) => {
        try {
            const r = await api.put(`/admin/patients/${id}/toggle-active`, { comment });
            toast.success(`${patient.name} ${r.data.is_active ? "activated" : "deactivated"}`);
            setToggleOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to toggle status");
            throw e;
        }
    };

    if (loading && !patient) return <div className="text-muted-foreground p-4">Loading…</div>;
    if (!patient) {
        return (
            <div className="bg-card border border-border rounded-2xl p-10 text-center">
                <Warning size={32} className="mx-auto mb-2 text-muted-foreground" />
                <h2 className="font-display text-xl font-bold mb-1">Patient not found.</h2>
                <Button onClick={() => navigate("/admin/patients")} variant="outline" className="rounded-full mt-3">Back to patients</Button>
            </div>
        );
    }

    const isDecommissioned = !!patient.decommissioned_at;

    return (
        <div data-testid="admin-patient-profile" className="space-y-5">
            <Link to="/admin/patients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="patient-profile-back-link">
                <ArrowLeft size={14} /> Back to patients
            </Link>

            <ProfileHeader patient={patient} onToggleActive={() => setToggleOpen(true)} isDecommissioned={isDecommissioned} />

            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.icon,
                    count:
                        t.id === "cases"        ? (patient.cases_count ?? 0) :
                        t.id === "appointments" ? (patient.appointments_count ?? 0) : null,
                }))}
                active={tab}
                onChange={setTab}
                testid="patient-profile-tabs"
                tabTestidBase="patient-tab"
                showZero
            />

            <div data-testid={`patient-tab-body-${tab}`}>
                {tab === "overview"     && <OverviewTab patient={patient} onSaved={load} isDecommissioned={isDecommissioned} onAfterDecommission={load} />}
                {tab === "cases"        && <CasesTab patientId={id} />}
                {tab === "appointments" && <AppointmentsTab patientId={id} />}
                {tab === "account"      && <AccountTab patient={patient} />}
                {tab === "sessions"     && <SessionsPanel memberId={id} endpoint={`/admin/users/${id}/sessions`} />}
                {tab === "audit"        && <AuditTab patientId={id} />}
            </div>

            <CommentRequiredDialog
                open={toggleOpen}
                onClose={() => setToggleOpen(false)}
                title={patient.is_active === false ? `Activate ${patient.name}?` : `Deactivate ${patient.name}?`}
                description={patient.is_active === false
                    ? "They will regain access immediately. Please note the reason for reactivation."
                    : "They will lose access immediately. All cases, appointments, and history are preserved. Please note the reason for deactivation."}
                ctaLabel={patient.is_active === false ? "Activate patient" : "Deactivate patient"}
                ctaVariant={patient.is_active === false ? "default" : "destructive"}
                onConfirm={toggleActive}
            />
        </div>
    );
}


/* ===========================================================================
   HEADER
   =========================================================================== */
function ProfileHeader({ patient, onToggleActive, isDecommissioned }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-6 bento-shadow">
            <div className="flex items-start gap-5 flex-wrap">
                {patient.photo_data_url ? (
                    <img
                        src={patient.photo_data_url}
                        alt={patient.name}
                        className="w-20 h-20 rounded-2xl object-cover border border-border ring-4 ring-secondary/30 shrink-0"
                        data-testid="patient-profile-avatar-img"
                    />
                ) : (
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 grid place-items-center font-display font-bold text-2xl text-primary shrink-0 ring-4 ring-secondary/30">
                        {initials(patient.name)}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="patient-profile-name">{patient.name}</h1>
                        {patient.patient_code && (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground font-mono font-bold">
                                {patient.patient_code}
                            </span>
                        )}
                        {isDecommissioned ? (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-rose-200 bg-rose-100 text-rose-700 font-bold inline-flex items-center gap-1">
                                <Warning size={10} weight="fill" /> Decommissioned
                            </span>
                        ) : patient.is_active ? (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700 font-bold">
                                Active
                            </span>
                        ) : (
                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-700 font-bold">
                                Deactivated
                            </span>
                        )}
                        <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold inline-flex items-center gap-1 ${patient.is_walk_in ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-sky-100 text-sky-700 border-sky-200"}`}>
                            {patient.is_walk_in ? <><Storefront size={10} weight="fill" /> Walk-in</> : <><Globe size={10} weight="fill" /> Online</>}
                        </span>
                        <HealthScorePill
                            score={patient.health_score}
                            tier={patient.health_tier}
                            signals={patient.health_signals}
                            size="sm"
                            testid="patient-profile-health-pill"
                        />
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5 mt-2">
                        {patient.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> <span className="font-mono">{patient.phone}</span></div>}
                        {patient.email && <div className="flex items-center gap-1.5"><Envelope size={12} /> {patient.email}</div>}
                        {patient.managed_by_doctor_name && (
                            <div className="flex items-center gap-1.5">
                                <Stethoscope size={12} />
                                Managed by <span className="font-semibold text-foreground">{patient.managed_by_doctor_name}</span> · {patient.managed_by_clinic_name}
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <QuickStat icon={ClipboardText} value={patient.cases_count} label="Cases" />
                    <QuickStat icon={Calendar} value={patient.appointments_count} label="Visits" />
                </div>

                {!isDecommissioned && (
                    <div className="flex items-center gap-2 self-start">
                        <Button
                            variant="outline" size="sm"
                            onClick={onToggleActive}
                            className={patient.is_active ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}
                            data-testid="patient-profile-toggle-active-btn"
                        >
                            <Power size={14} className="mr-1.5" />
                            {patient.is_active ? "Deactivate" : "Activate"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

const QuickStat = ({ icon: Icon, value, label }) => (
    <div className="bg-secondary/40 rounded-xl px-3 py-2 text-center min-w-[68px]">
        <Icon size={14} className="text-primary mx-auto mb-0.5" weight="duotone" />
        <div className="font-display font-bold text-lg leading-tight tabular-nums">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
    </div>
);


/* ===========================================================================
   TAB 1 — OVERVIEW (edit + danger zone)
   =========================================================================== */
function OverviewTab({ patient, onSaved, isDecommissioned, onAfterDecommission }) {
    const [form, setForm] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [decommissionBusy, setDecommissionBusy] = useState(false);

    useEffect(() => {
        setForm({
            name: patient.name || "",
            email: patient.email || "",
            dob: patient.dob || "",
            gender: patient.gender || "",
            notes: patient.notes || "",
            managed_by_doctor_id: patient.managed_by_doctor_id || "",
        });
    }, [patient]);

    useEffect(() => {
        // Lazy-load the list of doctors only once when this tab renders
        api.get("/admin/doctors").then((r) => setDoctors((r.data || []).filter((d) => d.is_active !== false))).catch(() => setDoctors([]));
    }, []);

    const dirty = useMemo(() => {
        return form.name !== (patient.name || "")
            || form.email !== (patient.email || "")
            || form.dob !== (patient.dob || "")
            || form.gender !== (patient.gender || "")
            || form.notes !== (patient.notes || "")
            || form.managed_by_doctor_id !== (patient.managed_by_doctor_id || "");
    }, [form, patient]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/patients/${patient.id}`, form);
            toast.success("Patient updated");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save");
        }
        setSaving(false);
    };

    const decommission = async () => {
        setDecommissionBusy(true);
        try {
            await api.put(`/admin/patients/${patient.id}/decommission`);
            toast.success(`${patient.name} decommissioned`);
            setConfirming(false);
            onAfterDecommission?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to decommission");
        }
        setDecommissionBusy(false);
    };

    return (
        <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
                <SectionCard icon={PencilSimple} title="Patient details">
                    {isDecommissioned && (
                        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 text-sm text-rose-800" data-testid="patient-decommissioned-notice">
                            <Warning size={14} weight="fill" className="inline mr-1.5" />
                            This patient is decommissioned. Editing is disabled. Decommissioned by{" "}
                            <strong>{patient.decommissioned_by || "—"}</strong> on{" "}
                            <strong>{fmtDate(patient.decommissioned_at)}</strong>.
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={isDecommissioned} className="h-11 mt-1.5" data-testid="patient-edit-name" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={isDecommissioned} className="h-11 mt-1.5" data-testid="patient-edit-email" />
                        </div>
                        <div>
                            <Label>Date of birth</Label>
                            <Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} disabled={isDecommissioned} className="h-11 mt-1.5" data-testid="patient-edit-dob" />
                        </div>
                        <div>
                            <Label>Gender</Label>
                            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} disabled={isDecommissioned}
                                className="w-full h-11 mt-1.5 rounded-md border border-input bg-background px-3 text-sm" data-testid="patient-edit-gender">
                                {GENDERS.map((g) => <option key={g} value={g}>{g || "Select…"}</option>)}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Managing doctor</Label>
                            <select value={form.managed_by_doctor_id} onChange={(e) => setForm({ ...form, managed_by_doctor_id: e.target.value })} disabled={isDecommissioned}
                                className="w-full h-11 mt-1.5 rounded-md border border-input bg-background px-3 text-sm" data-testid="patient-edit-doctor">
                                <option value="">— Unassigned —</option>
                                {doctors.map((d) => (
                                    <option key={d.id} value={d.id}>{d.name} · {d.clinic_name}</option>
                                ))}
                            </select>
                            <p className="text-xs text-muted-foreground mt-1.5">
                                Set or change which provider this patient is officially under.
                            </p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Notes</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} disabled={isDecommissioned} className="mt-1.5" data-testid="patient-edit-notes" />
                        </div>
                    </div>
                    <div className="flex justify-end pt-3 border-t border-border mt-5">
                        <Button onClick={save} disabled={!dirty || saving || isDecommissioned} data-testid="patient-edit-save-btn">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </SectionCard>
            </div>

            <div className="space-y-5">
                <SectionCard icon={Sparkle} title="Quick facts">
                    <dl className="text-sm space-y-2.5">
                        <KV label="Patient code" value={patient.patient_code} mono />
                        <KV label="Source"        value={patient.is_walk_in ? "Walk-in" : "Online (self-signup)"} />
                        <KV label="Created"       value={fmtDate(patient.created_at)} />
                        {patient.created_by_doctor_id && <KV label="Created by clinic" value={patient.created_by_doctor_id} mono />}
                    </dl>
                </SectionCard>

                {!isDecommissioned && (
                    <SectionCard icon={Trash} title="Danger zone" tone="rose">
                        <p className="text-xs text-muted-foreground mb-3">
                            Decommission permanently disables this patient. Cases, appointments, and invoices are <strong>not</strong> deleted —
                            they remain for audit and financial records. Reversible only by direct DB intervention.
                        </p>
                        {!confirming ? (
                            <Button variant="outline" size="sm" onClick={() => setConfirming(true)}
                                className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="patient-decommission-btn">
                                <Trash size={14} className="mr-1.5" /> Decommission patient
                            </Button>
                        ) : (
                            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 space-y-2.5" data-testid="patient-decommission-confirm">
                                <p className="text-xs text-rose-900">Confirm decommission for <strong>{patient.name}</strong>?</p>
                                <div className="flex items-center justify-end gap-2">
                                    <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} data-testid="patient-decommission-cancel-btn">Cancel</Button>
                                    <Button size="sm" onClick={decommission} disabled={decommissionBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="patient-decommission-confirm-btn">
                                        {decommissionBusy ? "Decommissioning…" : "Yes, decommission"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </SectionCard>
                )}
            </div>
        </div>
    );
}


/* ===========================================================================
   TAB 2 — CASES (read-only list of every case for this patient)
   =========================================================================== */
function CasesTab({ patientId }) {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/patients/${patientId}/cases`).then((r) => setCases(r.data)).finally(() => setLoading(false));
    }, [patientId]);

    const filterChips = useMemo(() => {
        const counts = {};
        cases.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
        const sorted = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
        return [{ id: "all", label: "All", count: cases.length }, ...sorted.map((s) => ({ id: s, label: s.replace(/_/g, " "), count: counts[s] }))];
    }, [cases]);

    const filtered = useMemo(() => {
        let list = cases;
        if (statusFilter !== "all") list = list.filter((c) => c.status === statusFilter);
        if (q.trim()) {
            const l = q.toLowerCase();
            list = list.filter((c) =>
                (c.code || "").toLowerCase().includes(l) ||
                (c.doctor_name || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [cases, statusFilter, q]);

    return (
        <SectionCard icon={ClipboardText} title={`Case history (${cases.length})`} right={
            <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or doctor…" className="pl-9 h-9 w-56" data-testid="patient-cases-search" />
            </div>
        }>
            {filterChips.length > 1 && (
                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="patient-cases-filter">
                    {filterChips.map((c) => {
                        const on = statusFilter === c.id;
                        return (
                            <button key={c.id} onClick={() => setStatusFilter(c.id)}
                                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors capitalize ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}
                                data-testid={`patient-cases-filter-${c.id}`}>
                                {c.label}
                                <span className={`ml-1.5 tabular-nums ${on ? "text-white/70" : "text-muted-foreground"}`}>{c.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && cases.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="patient-cases-empty">
                    No cases for this patient yet.
                </div>
            )}
            {!loading && cases.length > 0 && filtered.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">No cases match your search.</div>
            )}

            <div className="space-y-2">
                {filtered.map((c) => {
                    const cls = CASE_STATUS_STYLES[c.status] || "bg-secondary text-foreground border-border";
                    return (
                        <Link
                            key={c.id}
                            to={`/admin/cases/${c.id}`}
                            className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-secondary/30 hover:border-primary/30 transition"
                            data-testid={`patient-case-${c.id}`}
                        >
                            <ClipboardText size={16} className="text-primary shrink-0" weight="duotone" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm font-semibold">{c.code}</span>
                                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border capitalize ${cls}`}>
                                        {getStatusLabel(c.status)}
                                    </span>
                                    {c.stage_number != null && (
                                        <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">
                                            Stage {c.stage_number}{c.total_stages ? `/${c.total_stages}` : ""}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {c.doctor_name && c.doctor_name !== "—" ? c.doctor_name : "Provider: —"}
                                    {c.clinic_name && <span className="ml-1.5">· {c.clinic_name}</span>}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[11px] text-muted-foreground">{fmtDate(c.created_at)}</div>
                            </div>
                            <ArrowRight size={14} className="text-muted-foreground shrink-0" />
                        </Link>
                    );
                })}
            </div>
        </SectionCard>
    );
}


/* ===========================================================================
   TAB 3 — APPOINTMENTS
   =========================================================================== */
function AppointmentsTab({ patientId }) {
    const [appts, setAppts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/patients/${patientId}/appointments`).then((r) => setAppts(r.data)).finally(() => setLoading(false));
    }, [patientId]);

    return (
        <SectionCard icon={Calendar} title={`Visit history (${appts.length})`}>
            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && appts.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="patient-appointments-empty">
                    No appointments yet.
                </div>
            )}
            <div className="space-y-2">
                {appts.map((a) => {
                    const cls = APPT_STATUS_STYLES[a.status] || "bg-secondary text-foreground border-border";
                    return (
                        <div key={a.id} className="flex items-center gap-3 p-3 border border-border rounded-xl" data-testid={`patient-appt-${a.id}`}>
                            <Calendar size={16} className="text-primary shrink-0" weight="duotone" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm">{a.date}{a.time ? ` · ${a.time}` : ""}</span>
                                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border capitalize ${cls}`}>
                                        {getStatusLabel(a.status)}
                                    </span>
                                    {a.type && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground">{a.type}</span>}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {a.doctor_name}{a.clinic_name && ` · ${a.clinic_name}`}
                                </div>
                                {a.notes && <p className="text-xs text-muted-foreground mt-1 italic">"{a.notes}"</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </SectionCard>
    );
}


/* ===========================================================================
   TAB 4 — ACCOUNT (login / source / metadata, mostly read-only)
   =========================================================================== */
function AccountTab({ patient }) {
    return (
        <div className="grid sm:grid-cols-2 gap-5" data-testid="patient-account-content">
            <SectionCard icon={Crown} title="Login account">
                <dl className="text-sm space-y-2.5">
                    <KV label="Phone (login)" value={patient.phone} mono />
                    <KV label="Email"         value={patient.email} />
                    <KV label="Last login"    value={fmtDateTime(patient.last_login_at)} />
                    <KV label="Login active"  value={patient.is_active ? "Yes" : "No"} />
                </dl>
                {patient.is_walk_in && (
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                        Walk-in patients are created by a clinic. They can still log in via OTP if they have a phone on file.
                    </p>
                )}
            </SectionCard>

            <SectionCard icon={Stethoscope} title="Care attribution">
                <dl className="text-sm space-y-2.5">
                    <KV label="Managing doctor" value={
                        patient.managed_by_doctor_id ? (
                            <Link to={`/admin/doctors/${patient.managed_by_doctor_id}`} className="text-primary hover:underline">
                                {patient.managed_by_doctor_name || patient.managed_by_doctor_id}
                            </Link>
                        ) : "—"
                    } />
                    <KV label="Clinic"          value={patient.managed_by_clinic_name} />
                    <KV label="City"            value={patient.managed_by_clinic_city} />
                    <KV label="Created via"     value={patient.created_via || (patient.is_walk_in ? "Clinic walk-in" : "Self signup")} />
                </dl>
            </SectionCard>
        </div>
    );
}


/* ===========================================================================
   TAB 5 — AUDIT LOG
   =========================================================================== */
function AuditTab({ patientId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/patients/${patientId}/audit-log`).then((r) => setEntries(r.data)).finally(() => setLoading(false));
    }, [patientId]);

    return (
        <SectionCard icon={ClockClockwise} title={`Audit log (${entries.length})`}>
            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && entries.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8" data-testid="patient-audit-empty">
                    No audit entries yet. Any admin action (edit, decommission, doctor reassignment) will show up here.
                </div>
            )}
            <ul className="space-y-3">
                {entries.map((e) => (
                    <li key={e.id} className="flex gap-3" data-testid={`patient-audit-${e.id}`}>
                        <div className="shrink-0 w-8 h-8 rounded-full bg-secondary grid place-items-center text-xs font-bold text-muted-foreground">
                            {(e.actor_name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <div className="text-xs font-semibold">
                                    {e.actor_name || "System"}
                                    <span className="ml-2 text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{(e.action || "").replace(/_/g, " ")}</span>
                                </div>
                                <div className="text-[11px] text-muted-foreground tabular-nums">{fmtDateTime(e.created_at)}</div>
                            </div>
                            {e.summary && <p className="text-sm mt-0.5">{e.summary}</p>}
                            {e.meta && Object.keys(e.meta).length > 0 && (
                                <pre className="text-[11px] font-mono text-muted-foreground mt-1 break-all whitespace-pre-wrap">{JSON.stringify(e.meta)}</pre>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </SectionCard>
    );
}


/* ===========================================================================
   Atoms
   =========================================================================== */
function SectionCard({ icon: Icon, title, right, tone, children }) {
    const cls = tone === "rose" ? "bg-rose-50 border-rose-200" : "bg-card border-border";
    return (
        <div className={`rounded-2xl border p-5 ${cls}`}>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className={`flex items-center gap-2 ${tone === "rose" ? "text-rose-800" : ""}`}>
                    <Icon size={16} weight="duotone" />
                    <span className="text-xs uppercase tracking-wide font-bold">{title}</span>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

function KV({ label, value, mono }) {
    return (
        <div>
            <dt className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-0.5">{label}</dt>
            <dd className={mono ? "font-mono text-sm break-all" : "text-sm break-words"}>
                {value === null || value === undefined || value === "" ? <span className="text-muted-foreground">—</span> : value}
            </dd>
        </div>
    );
}
